import { BulkEmailRequest, Email } from '../models';
import { ResourceWithAttachments } from './attachments';
import type { AxiosResponse } from 'axios';

export interface CreateEmailData {
  email_sender?: string;

  email_template?: string;

  partner?: string;

  invoices?: string[];

  credit_memos?: string[];

  options: {
    include_checkout_url?: boolean;
    reply_all?: boolean;
    use_llm?: boolean;
  };

  parent_email?: string;
}

export interface ForwardEmailData {
  to?: string[];

  cc?: string[];

  bcc?: string[];

  note?: string;

  subject?: string;
}

export interface GenerateEmailBodyData {
  email_template?: string;

  options: {
    include_checkout_url?: boolean;
    reply_all?: boolean;
    use_llm?: boolean;
  };
}

export class Emails extends ResourceWithAttachments<Email, CreateEmailData> {
  model = Email;

  resourcePath = 'v1/emails';

  async bulkCreate(data: {
    auto_send?: boolean;
    email_sender?: string;
    email_template?: string;
    partners?: string[];
  }): Promise<BulkEmailRequest> {
    return this.api.post(this.path('bulk/create'), { data }).then((response) => {
      return BulkEmailRequest.fromJson(response.data);
    });
  }

  async bulkDelete(data: { emails: string[] }): Promise<AxiosResponse> {
    return this.api.post(this.path('bulk/delete'), { data });
  }

  async bulkSend(data: { emails: string[] }): Promise<AxiosResponse> {
    return this.api.post(this.path('bulk/send'), { data });
  }

  async markAsRead(emailId: string, email?: Email): Promise<Email> {
    const response = await this.api.post(this.path(emailId, 'read'));
    if (email) {
      email.updateFromJson(response.data);
    } else {
      email = Email.fromJson(response.data);
    }
    return email;
  }

  async send(emailId: string, email?: Email): Promise<Email> {
    if (email && email.id !== emailId) {
      throw Error(`${emailId} does not match provided email with id ${email.id}`);
    }

    // eslint-disable-next-line
    return this.api.post(this.path(emailId, 'send'), {}).then((res) => {
      if (email) {
        email.updateFromJson(res.data);
        return email;
      }

      return Email.fromJson(res.data);
    });
  }

  async forward(emailId: string, data: ForwardEmailData = {}, email?: Email): Promise<Email> {
    if (email && email.id !== emailId) {
      throw Error(`${emailId} does not match provided email with id ${email.id}`);
    }

    // eslint-disable-next-line
    return this.api.post(this.path(emailId, 'forward'), { data }).then((res) => {
      if (email) {
        email.updateFromJson(res.data);
        return email;
      }

      return Email.fromJson(res.data);
    });
  }

  async generateBody(emailId: string, data: GenerateEmailBodyData, email?: Email): Promise<Email> {
    if (email && email.id !== emailId) {
      throw Error(`${emailId} does not match provided email with id ${email.id}`);
    }

    // eslint-disable-next-line
    return this.api.post(this.path(emailId, 'generate-body'), { data }).then((res) => {
      if (email) {
        email.updateFromJson(res.data);
        return email;
      }

      return Email.fromJson(res.data);
    });
  }

  async attachInvoice(email: Email, invoice_id: string): Promise<Email> {
    const response = await this.api.post(this.path(email?.id, 'attach-invoice'), {
      data: { invoice_id },
    });

    email.updateFromJson(response.data);

    return email;
  }

  async attachCreditMemo(email: Email, credit_memo_id: string): Promise<Email> {
    const response = await this.api.post(this.path(email?.id, 'attach-credit-memo'), {
      data: { credit_memo_id },
    });

    email.updateFromJson(response.data);

    return email;
  }
}
